var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as piexif from 'piexifjs';
import { EMPTY_TEXT } from '@mitchell/react.component.ux';
import { FileService, allFilesUploadSuccessAction, allFilesUploadStartAction } from "../../file";
import { dataURLtoFile, getLatitudeLongitude } from "../../common";
import { PhotoCaptureConstants, PhotoCaptureAllowedMimeTypeConstant } from '../constants';
import { photoCaptureIntelligentPhotoUploadSuccessAction, photoCaptureIntelligentPhotoUploadFailureAction } from '../actions';
import { isPrgCompanyCode } from './photoCaptureCommonUtilities';
export const photoCaptureIntelligentUploadCapturedPhoto = (photo, companyCode, fileSizeLimit, fileDescription, imageMaxWidth, dispatch, onFileUploadSuccess, isMobile) => __awaiter(void 0, void 0, void 0, function* () {
    const files = [photo.file];
    const uploadFiles = Object.assign({ isDuplicateDocumentAllowed: false, allowedFileTypes: PhotoCaptureAllowedMimeTypeConstant, fileList: files, companyCode: companyCode, applicationName: PhotoCaptureConstants.PHOTO_CAPTURE_APPLICATION_NAME, onAllFileUploadComplete: onAllFilesDataUploadComplete(dispatch, isMobile, onFileUploadSuccess), onFilesReady: onFilesReadyUpload(dispatch), onFileUploadStartFailure: onFileUploadStartFailure(dispatch), thumbnailMaxSize: PhotoCaptureConstants.THUMBNAIL_MAX_SIZE, maxFileSize: fileSizeLimit, scaledMaxSize: imageMaxWidth }, (isPrgCompanyCode(companyCode) && { fileDescription }));
    FileService.uploadFiles(uploadFiles);
});
export const onAllFilesDataUploadComplete = (dispatch, isMobile, onFileUploadSuccess) => {
    return (files) => {
        dispatch(photoCaptureIntelligentPhotoUploadSuccessAction(files));
        dispatch(allFilesUploadSuccessAction(isMobile));
        if (onFileUploadSuccess) {
            onFileUploadSuccess(files);
        }
    };
};
export const onFilesReadyUpload = (dispatch) => {
    return (uploadFilesActionPayload) => {
        dispatch(allFilesUploadStartAction(uploadFilesActionPayload));
    };
};
export const onFileUploadStartFailure = (dispatch) => {
    return (error) => {
        dispatch(photoCaptureIntelligentPhotoUploadFailureAction(error));
    };
};
/**
 * Adds GPS location data to an image by embedding EXIF metadata
 * @param imageBase64 - Base64 encoded image data with EXIF container
 * @param fileName - Name to use for the output file
 * @returns Promise resolving to a File object containing the image with GPS data
 */
export const addGpsToImage = (imageBase64, fileName) => __awaiter(void 0, void 0, void 0, function* () {
    let exifObj;
    try {
        exifObj = piexif.load(imageBase64);
    }
    catch (e) {
        exifObj = {
            '0th': {},
            Exif: {},
            GPS: {},
            Interop: {},
            '1st': {},
            thumbnail: EMPTY_TEXT
        };
    }
    try {
        const { absoluteLatitude, absoluteLongitude, latitudeRef, longitudeRef } = yield getLatitudeLongitude();
        // Convert decimal lat/long to DMS format
        const latDMS = decimalToDMS(Math.abs(absoluteLatitude));
        const longDMS = decimalToDMS(Math.abs(absoluteLongitude));
        // Set GPS data
        exifObj.GPS = {
            [piexif.GPSIFD.GPSVersionID]: [2, 2, 0, 0],
            [piexif.GPSIFD.GPSLatitudeRef]: latitudeRef,
            [piexif.GPSIFD.GPSLatitude]: [
                [latDMS.degrees, 1],
                [latDMS.minutes, 1],
                [latDMS.seconds * 10000, 10000]
            ],
            [piexif.GPSIFD.GPSLongitudeRef]: longitudeRef,
            [piexif.GPSIFD.GPSLongitude]: [
                [longDMS.degrees, 1],
                [longDMS.minutes, 1],
                [longDMS.seconds * 10000, 10000]
            ],
            // Add timestamp
            [piexif.GPSIFD.GPSDateStamp]: new Date()
                .toISOString()
                .split('T')[0]
                .replace(/-/g, ':')
        };
        // Convert EXIF object to binary and insert into image
        const exifBytes = piexif.dump(exifObj);
        const fileDataUrlWithExif = piexif.insert(exifBytes, imageBase64);
        const resultFile = dataURLtoFile(fileDataUrlWithExif, fileName);
        if (!resultFile) {
            throw new Error('Error occurs when convert JPEG image with EXIF to File');
        }
        return resultFile;
    }
    catch (error) {
        // Geolocation is disabled or unavailable, return image without GPS data
        const resultFile = dataURLtoFile(imageBase64, fileName);
        if (!resultFile) {
            throw new Error('Error occurs when convert JPEG image to File');
        }
        return resultFile;
    }
});
/**
 * Converts decimal coordinates to Degrees, Minutes, Seconds format
 * @param decimal - Decimal degrees value to convert
 * @returns Object containing degrees, minutes, and seconds components
 */
const decimalToDMS = (decimal) => {
    const degrees = Math.floor(decimal);
    const minutesDecimal = (decimal - degrees) * 60;
    const minutes = Math.floor(minutesDecimal);
    const seconds = (minutesDecimal - minutes) * 60;
    return {
        degrees: degrees,
        minutes: minutes,
        seconds: seconds
    };
};
/**
 * Uploads estimator files for intelligent photo capture.
 *
 * @param file - Array of File objects to upload
 * @param allowedFileTypes - Array of supported file types for validation
 * @param companyCode - The company code for the upload context
 * @param fileSizeLimit - Maximum allowed file size in bytes
 * @param fileDescription - Description to associate with the uploaded files
 * @param imageMaxWidth - Maximum width for image scaling
 * @param maxFileCount - Maximum number of files allowed in the upload
 * @param dispatch - Redux dispatch function for triggering actions (used for upload lifecycle events)
 * @param onFileUploadSuccess - Optional callback invoked when all files are uploaded successfully
 * @param isMobile - Optional flag indicating if upload is from a mobile device
 */
export const photoCaptureIntelligentEstimatorUpload = (file, allowedFileTypes, companyCode, fileSizeLimit, fileDescription, imageMaxWidth, maxFileCount, dispatch, onFileUploadSuccess, isMobile) => __awaiter(void 0, void 0, void 0, function* () {
    const uploadFiles = Object.assign(Object.assign({ isDuplicateDocumentAllowed: true, allowedFileTypes, fileList: file, companyCode: companyCode, scaledMaxSize: imageMaxWidth, applicationName: PhotoCaptureConstants.PHOTO_CAPTURE_APPLICATION_NAME, onAllFileUploadComplete: onAllFilesEstimatorDataUploadComplete(dispatch, isMobile, onFileUploadSuccess), onFilesReady: onFilesReadyUpload(dispatch), onFileUploadStartFailure: onFileUploadStartFailure(dispatch), thumbnailMaxSize: PhotoCaptureConstants.THUMBNAIL_MAX_SIZE, maxFileSize: fileSizeLimit }, (maxFileCount > 0 && { maxFileCount })), (isPrgCompanyCode(companyCode) && { fileDescription }));
    FileService.uploadFiles(uploadFiles);
});
/**
 * Returns a callback to be used when all estimator files have been uploaded.
 * Invokes the provided onFileUploadSuccess callback if present.
 *
 * @param dispatch - Redux dispatch function for triggering upload success actions
 * @param isMobile - Optional flag indicating if upload is from a mobile device
 * @param onFileUploadSuccess - Optional callback to invoke with the uploaded files array
 * @returns Callback function to handle successful upload completion
 */
export const onAllFilesEstimatorDataUploadComplete = (dispatch, isMobile, onFileUploadSuccess) => {
    return (files) => {
        dispatch(allFilesUploadSuccessAction(isMobile));
        if (onFileUploadSuccess) {
            onFileUploadSuccess(files);
        }
    };
};
//# sourceMappingURL=fileUploadHelper.js.map